/**
 * useSyncToasts
 *
 * Hook that shows toast notifications for sync events.
 * Automatically displays toasts when:
 * - Going offline
 * - Coming back online
 * - Sync completing
 * - Sync errors
 */

import { useEffect, useRef } from 'react';
import { toast, Id } from 'react-toastify';

import { useTranslation } from 'common/hooks/useTranslation';
import { syncManager } from './syncManager';
import { SyncEvent } from './types';
import { useOnlineStatus } from './useOnlineStatus';

export type UseSyncToastsOptions = {
  /** Show toast when going offline */
  showOfflineToast?: boolean;
  /** Show toast when coming back online */
  showOnlineToast?: boolean;
  /** Show toast when sync starts */
  showSyncStartToast?: boolean;
  /** Show toast when sync completes */
  showSyncCompleteToast?: boolean;
  /** Show toast on sync errors */
  showSyncErrorToast?: boolean;
};

const DEFAULT_OPTIONS: UseSyncToastsOptions = {
  showOfflineToast: true,
  showOnlineToast: true,
  showSyncStartToast: false, // Can be noisy
  showSyncCompleteToast: true,
  showSyncErrorToast: true,
};

/**
 * Hook that displays toast notifications for sync events
 *
 * @example
 * ```tsx
 * // In your app root or layout
 * const App = () => {
 *   useSyncToasts();
 *   return <AppContent />;
 * };
 * ```
 */
export function useSyncToasts(options: UseSyncToastsOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { t } = useTranslation();
  const { isOnline, wasOffline, clearWasOffline } = useOnlineStatus();
  const syncToastId = useRef<Id | null>(null);
  const wasOnlineRef = useRef(isOnline);

  // Handle offline/online status changes
  useEffect(() => {
    // Going offline
    if (!isOnline && wasOnlineRef.current && opts.showOfflineToast) {
      toast.warning(t('sync.toast.offline'), {
        autoClose: false,
        toastId: 'offline-toast',
      });
    }

    // Coming back online
    if (isOnline && !wasOnlineRef.current && opts.showOnlineToast) {
      // Dismiss offline toast
      toast.dismiss('offline-toast');

      toast.info(t('sync.toast.online'), {
        autoClose: 3000,
        toastId: 'online-toast',
      });
    }

    wasOnlineRef.current = isOnline;
  }, [isOnline, opts.showOfflineToast, opts.showOnlineToast, t]);

  // Handle sync events
  useEffect(() => {
    const unsubscribe = syncManager.subscribe((event: SyncEvent) => {
      switch (event.type) {
        case 'SYNC_STARTED':
          if (opts.showSyncStartToast && event.payload.total > 0) {
            syncToastId.current = toast.loading(t('sync.toast.syncing'), {
              toastId: 'sync-progress-toast',
            });
          }
          break;

        case 'SYNC_COMPLETED':
          // Dismiss loading toast
          if (syncToastId.current) {
            toast.dismiss(syncToastId.current);
            syncToastId.current = null;
          }

          if (opts.showSyncCompleteToast) {
            const { successful, failed } = event.payload;

            if (failed > 0) {
              toast.warning(t('sync.toast.completedWithErrors', { successful, failed }), { autoClose: 5000 });
            } else if (successful > 0) {
              toast.success(t('sync.toast.completed', { count: successful }), { autoClose: 3000 });
            }
          }

          // Clear wasOffline flag after successful sync
          if (wasOffline) {
            clearWasOffline();
          }
          break;

        case 'SYNC_ERROR':
          if (opts.showSyncErrorToast) {
            toast.error(t('sync.toast.error', { error: event.payload.error }), { autoClose: 5000 });
          }
          break;

        case 'MUTATION_QUEUED':
          // Optional: Show subtle notification when mutation is queued offline
          // This is handled by individual hooks with onOfflineQueued callback
          break;

        default:
          // Handle other event types
          break;
      }
    });

    return unsubscribe;
  }, [opts, t, wasOffline, clearWasOffline]);
}

/**
 * Show a toast when a mutation is queued offline
 * Use this in onOfflineQueued callbacks
 */
export function showOfflineQueuedToast(
  t: (key: string, values?: Record<string, string | number>) => string,
  entity?: string,
) {
  const message = entity ? t('sync.toast.queuedEntity', { entity }) : t('sync.toast.queued');

  toast.info(message, {
    autoClose: 2000,
    toastId: `queued-${Date.now()}`,
  });
}
